import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronRight, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const useModuleExtras = (moduleId, authHeader) => {
  const [extras, setExtras] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!moduleId) return undefined;
    axios.get(`${API}/api/learning/module-extras/${moduleId}`, authHeader)
      .then((r) => { if (alive) setExtras(r.data?.available ? r.data : null); })
      .catch(() => { if (alive) setExtras(null); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);
  return extras;
};

const Flashcard = ({ card, index }) => {
  const [flipped, setFlipped] = useState(false);
  return (
    <button
      onClick={() => setFlipped(!flipped)}
      className="text-left w-full rounded-2xl border p-5 transition-colors"
      style={{ backgroundColor: flipped ? '#3A2A22' : '#F7F1E6', borderColor: flipped ? '#3A2A22' : '#DACFBE', minHeight: 110 }}
      data-testid={`flashcard-${index + 1}`}
    >
      <p className="text-[10px] uppercase font-bold mb-2 flex items-center gap-1.5"
        style={{ letterSpacing: '0.22em', color: flipped ? '#D89232' : '#8F650F' }}>
        <RotateCcw className="w-3 h-3" /> Karte {index + 1} · {flipped ? 'Antwort' : 'Frage'}
      </p>
      <p className="text-[14px] leading-relaxed" style={{ color: flipped ? '#F5F0EA' : '#3A2A22' }}>
        {flipped ? card.back : card.front}
      </p>
    </button>
  );
};

const RepetitionItem = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-cream-300 bg-cream-100 overflow-hidden" data-testid={`repetition-${index + 1}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-start justify-between gap-3 px-5 py-4 text-left">
        <span className="text-[14px] font-semibold text-espresso leading-snug">{index + 1}. {item.question}</span>
        <ChevronRight className={`w-4 h-4 mt-0.5 flex-shrink-0 text-terra transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <p className="px-5 pb-4 text-[13.5px] text-sand-700 leading-relaxed border-t border-cream-300 pt-3"
          style={{ backgroundColor: '#EEF3EA' }}>{item.answer}</p>
      )}
    </div>
  );
};

const SectionHeader = ({ kicker, title }) => (
  <div className="mb-5">
    <p className="text-[10.5px] uppercase font-bold mb-1" style={{ letterSpacing: '0.26em', color: '#8F650F' }}>{kicker}</p>
    <h3 className="font-display text-[24px] text-espresso">{title}</h3>
  </div>
);

const ModuleExtras = ({ extras, renderMarkdown }) => {
  if (!extras) return null;
  return (
    <div className="space-y-10" data-testid="module-extras">
      <p className="text-[13px] text-sand-600 leading-relaxed -mb-2">
        Vertiefendes Selbststudium zu diesem Modul — nimm dir Zeit für die Fallbeispiele und
        wiederhole mit den Lernkarten. Dieses Material gehört zu deinen Unterrichtseinheiten.
      </p>

      {extras.deep_dive_md && (
        <div>
          <SectionHeader kicker="Selbststudium" title="Vertiefung." />
          <div className="bg-cream-100 rounded-2xl border border-cream-300 p-6 md:p-8">
            <div className="dh-article max-w-none">{renderMarkdown(extras.deep_dive_md)}</div>
          </div>
        </div>
      )}

      {(extras.case_studies || []).length > 0 && (
        <div>
          <SectionHeader kicker="Praxistransfer" title="Fallbeispiele." />
          <div className="space-y-5">
            {extras.case_studies.map((cs, i) => (
              <div key={i} className="rounded-2xl border p-6 md:p-7" style={{ backgroundColor: '#F7F1E6', borderColor: '#DACFBE' }} data-testid={`case-study-${i + 1}`}>
                <p className="font-display italic text-[19px] mb-3" style={{ color: '#E07A5F' }}>Fall {i + 1}: {cs.title}</p>
                <div className="dh-article max-w-none">{renderMarkdown(cs.text_md)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(extras.flashcards || []).length > 0 && (
        <div>
          <SectionHeader kicker="Wiederholen & festigen" title="Lernkarten." />
          <p className="text-[12.5px] text-sand-500 mb-4">Tippe auf eine Karte, um die Antwort zu sehen.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {extras.flashcards.map((c, i) => <Flashcard key={i} card={c} index={i} />)}
          </div>
        </div>
      )}

      {(extras.repetition_questions || []).length > 0 && (
        <div>
          <SectionHeader kicker="Selbst prüfen" title="Wiederholungsfragen." />
          <div className="space-y-3">
            {extras.repetition_questions.map((r, i) => <RepetitionItem key={i} item={r} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModuleExtras;
